import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, Spinner } from '../../../shared/components';
import { createEmployee, getEmployeesMetadata } from '../services/employeeService';
import { CreateEmployeeRequest, EmployeesMetadata } from '../types/Employee';
import {
  buildDepartmentLabelMap,
  buildHierarchicalDepartmentOptions,
  formatDepartmentOptionLabel,
} from '../utils/departmentOptions';
import styles from './EmployeeLayout.module.css';

const DEFAULT_COLOR = '#2563EB';

const resolveEmployeeColor = (colorPreference?: string): string => {
  const fallback = '#94a3b8';
  const value = (colorPreference || '').trim();
  if (!value) return fallback;

  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', value) ? value : fallback;
  }

  return value;
};

const buildPositionOptions = (
  positions: EmployeesMetadata['positions'],
  departmentLabelById: Map<string, string>,
) => {

  return positions
    .map(item => ({
      id: item.id,
      departmentId: item.departmentId,
      label: item.departmentId
        ? `${departmentLabelById.get(item.departmentId) || item.departmentName || ''} / ${item.name}`
        : item.name,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

export const AddEmployee: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const [loadingMetadata, setLoadingMetadata] = React.useState(true);
  const [metadata, setMetadata] = React.useState<EmployeesMetadata | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<CreateEmployeeRequest>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    title: '',
    dateOfBirth: '',
    employmentDate: '',
    status: '',
    contractTypeId: '',
    positionId: '',
    departmentId: '',
    phoneCountryCode: '',
    phoneNumber: '',
    pbxExtension: '',
    language: '',
    calendarPreference: '',
    locationId: '',
    colorPreference: DEFAULT_COLOR,
    isActive: true,
    requirePasswordChangeOnFirstLogin: true,
  });
  const [errors, setErrors] = React.useState<{ [k: string]: string }>({});
  const departmentOptions = React.useMemo(
    () => buildHierarchicalDepartmentOptions(metadata?.departments || []),
    [metadata?.departments],
  );
  const departmentLabelById = React.useMemo(
    () => buildDepartmentLabelMap(metadata?.departments || []),
    [metadata?.departments],
  );
  const positionOptions = React.useMemo(
    () => buildPositionOptions(metadata?.positions || [], departmentLabelById),
    [departmentLabelById, metadata?.positions],
  );
  const availablePositions = React.useMemo(() => {
    const selectedDepartmentId = (form.departmentId || '').trim();
    return positionOptions.filter(option => !option.departmentId || !selectedDepartmentId || option.departmentId === selectedDepartmentId);
  }, [form.departmentId, positionOptions]);

  React.useEffect(() => {
    if (!form.positionId) {
      return;
    }

    const stillAvailable = availablePositions.some(option => option.id === form.positionId);
    if (!stillAvailable) {
      setForm(prev => ({ ...prev, positionId: '' }));
    }
  }, [availablePositions, form.positionId]);

  React.useEffect(() => {
    const loadMetadata = async () => {
      setLoadingMetadata(true);
      try {
        const data = await getEmployeesMetadata();
        setMetadata(data);
        setForm(prev => ({
          ...prev,
          status: prev.status || data.statuses[0]?.value || '',
          language: prev.language || data.languages[0]?.code || '',
          calendarPreference: prev.calendarPreference || data.calendarPreferences[0]?.value || '',
        }));
        setGeneralError(null);
      } catch {
        setGeneralError(t('failedToLoadEmployeeMetadata', { ns: 'admin' }));
      } finally {
        setLoadingMetadata(false);
      }
    };

    void loadMetadata();
  }, [t]);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('admin', { ns: 'admin' }), path: '/admin' },
    { label: t('teamMembers', { ns: 'admin' }), path: '/members' },
    { label: t('addEmployee', { ns: 'admin' }), path: '/members/add' },
  ];

  const validate = () => {
    const nextErrors: { [k: string]: string } = {};

    if (!(form.firstName || '').trim() && !(form.lastName || '').trim()) {
      nextErrors.firstName = t('nameRequired', { ns: 'admin' });
    }

    const email = (form.email || '').trim();
    if (!email) {
      nextErrors.email = t('emailRequired', { ns: 'admin' });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = t('invalidEmailAddress', { ns: 'admin' });
    }

    const password = form.password || '';
    if (!password.trim()) {
      nextErrors.password = t('passwordRequired', { ns: 'admin' });
    } else if (password.length < 8) {
      nextErrors.password = t('passwordMinLength', { ns: 'admin' });
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setGeneralError(null);

    try {
      await createEmployee({
        firstName: (form.firstName || '').trim() || undefined,
        lastName: (form.lastName || '').trim() || undefined,
        email: (form.email || '').trim(),
        password: form.password,
        title: (form.title || '').trim() || undefined,
        dateOfBirth: (form.dateOfBirth || '').trim() || undefined,
        employmentDate: (form.employmentDate || '').trim() || undefined,
        status: (form.status || '').trim() || undefined,
        contractTypeId: (form.contractTypeId || '').trim() || undefined,
        positionId: (form.positionId || '').trim() || undefined,
        departmentId: (form.departmentId || '').trim() || undefined,
        phoneCountryCode: (form.phoneCountryCode || '').trim() || undefined,
        phoneNumber: (form.phoneNumber || '').trim() || undefined,
        pbxExtension: (form.pbxExtension || '').trim() || undefined,
        language: (form.language || '').trim() || undefined,
        calendarPreference: (form.calendarPreference || '').trim() || undefined,
        locationId: (form.locationId || '').trim() || undefined,
        colorPreference: (form.colorPreference || '').trim() || undefined,
        isActive: form.isActive ?? true,
        requirePasswordChangeOnFirstLogin: form.requirePasswordChangeOnFirstLogin ?? true,
      });
      navigate('/members');
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToAddEmployee', { ns: 'admin' }));
    } finally {
      setSaving(false);
    }
  };

  if (loadingMetadata) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Spinner text={t('loadingEmployeeMetadata', { ns: 'admin' })} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={breadcrumbItems} />
      <div className={styles.header}>
        <h2 className={styles.title}>{t('addEmployee', { ns: 'admin' })}</h2>
      </div>

      {generalError && <div className={`error ${styles.errorBanner}`}>{generalError}</div>}

      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label className={styles.label}>
            {t('firstName', { ns: 'admin' })}
            <input
              className={styles.input}
              value={form.firstName || ''}
              onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))}
            />
          </label>
          {errors.firstName && <div className={styles.fieldError}>{errors.firstName}</div>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('lastName', { ns: 'admin' })}
            <input
              className={styles.input}
              value={form.lastName || ''}
              onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('email', { ns: 'admin' })}
            <input
              className={styles.input}
              value={form.email || ''}
              onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
            />
          </label>
          {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
        </div>

        <div className={`${styles.field} ${styles.fieldFull}`}>
          <label className={styles.label}>
            {t('password', { ns: 'admin' })}
            <input
              className={styles.input}
              type="password"
              value={form.password || ''}
              onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
            />
          </label>
          {errors.password && <div className={styles.fieldError}>{errors.password}</div>}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('title', { ns: 'admin' })}
            <input
              className={styles.input}
              value={form.title || ''}
              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('position', { ns: 'admin' })}
            <select
              className={`${styles.input} ${styles.select}`}
              value={form.positionId || ''}
              onChange={e => setForm(prev => ({ ...prev, positionId: e.target.value }))}
            >
              <option value="">{t('selectPosition', { ns: 'admin' })}</option>
              {availablePositions.map(option => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('contractType', { ns: 'admin' })}
            <select
              className={`${styles.input} ${styles.select}`}
              value={form.contractTypeId || ''}
              onChange={e => setForm(prev => ({ ...prev, contractTypeId: e.target.value }))}
            >
              <option value="">{t('selectContractType', { ns: 'admin' })}</option>
              {(metadata?.contractTypes || []).map(option => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('department', { ns: 'admin' })}
            <select
              className={`${styles.input} ${styles.select}`}
              value={form.departmentId || ''}
              onChange={e => setForm(prev => ({ ...prev, departmentId: e.target.value }))}
            >
              <option value="">{t('selectDepartment', { ns: 'admin' })}</option>
              {departmentOptions.map(option => (
                <option
                  key={option.id}
                  value={option.id}
                  className={option.hasChildren ? styles.departmentParentOption : undefined}
                  style={option.hasChildren ? { fontWeight: 700 } : undefined}
                >
                  {formatDepartmentOptionLabel(option.name, option.depth)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('location', { ns: 'admin' })}
            <select
              className={`${styles.input} ${styles.select}`}
              value={form.locationId || ''}
              onChange={e => setForm(prev => ({ ...prev, locationId: e.target.value }))}
            >
              <option value="">{t('selectLocation', { ns: 'admin' })}</option>
              {(metadata?.locations || []).map(option => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('language', { ns: 'admin' })}
            <select
              className={`${styles.input} ${styles.select}`}
              value={form.language || ''}
              onChange={e => setForm(prev => ({ ...prev, language: e.target.value }))}
            >
              <option value="">{t('selectLanguage', { ns: 'admin' })}</option>
              {(metadata?.languages || []).map(option => (
                <option key={option.code} value={option.code}>{option.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('calendarPreference', { ns: 'admin' })}
            <select
              className={`${styles.input} ${styles.select}`}
              value={form.calendarPreference || ''}
              onChange={e => setForm(prev => ({ ...prev, calendarPreference: e.target.value }))}
            >
              <option value="">{t('selectCalendarPreference', { ns: 'admin' })}</option>
              {(metadata?.calendarPreferences || []).map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('employmentStatus', { ns: 'admin' })}
            <select
              className={`${styles.input} ${styles.select}`}
              value={form.status || ''}
              onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">{t('selectEmploymentStatus', { ns: 'admin' })}</option>
              {(metadata?.statuses || []).map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('employmentDate', { ns: 'admin' })}
            <input
              className={styles.input}
              type="date"
              value={form.employmentDate || ''}
              onChange={e => setForm(prev => ({ ...prev, employmentDate: e.target.value }))}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('dateOfBirth', { ns: 'admin' })}
            <input
              className={styles.input}
              type="date"
              value={form.dateOfBirth || ''}
              onChange={e => setForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('phoneCountryCode', { ns: 'admin' })}
            <input
              className={styles.input}
              value={form.phoneCountryCode || ''}
              onChange={e => setForm(prev => ({ ...prev, phoneCountryCode: e.target.value }))}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('phoneNumber', { ns: 'admin' })}
            <input
              className={styles.input}
              value={form.phoneNumber || ''}
              onChange={e => setForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('pbxExtension', { ns: 'admin' })}
            <input
              className={styles.input}
              value={form.pbxExtension || ''}
              onChange={e => setForm(prev => ({ ...prev, pbxExtension: e.target.value }))}
              placeholder={t('pbxExtensionPlaceholder', { ns: 'admin' })}
            />
          </label>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>{t('colorPreference', { ns: 'admin' })}</label>
          <div className={styles.colorField}>
            <input
              className={styles.colorInput}
              type="color"
              value={form.colorPreference || DEFAULT_COLOR}
              onChange={e => setForm(prev => ({ ...prev, colorPreference: e.target.value }))}
            />
            <span className={styles.colorValue}>{form.colorPreference || DEFAULT_COLOR}</span>
            <span className={styles.colorDot} style={{ backgroundColor: resolveEmployeeColor(form.colorPreference), boxShadow: `0 0 0 2px ${resolveEmployeeColor(form.colorPreference)}22` }} />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t('status', { ns: 'admin' })}
            <select
              className={`${styles.input} ${styles.select}`}
              value={(form.isActive ?? true) ? 'active' : 'inactive'}
              onChange={e => setForm(prev => ({ ...prev, isActive: e.target.value === 'active' }))}
            >
              <option value="active">{t('active', { ns: 'admin' })}</option>
              <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
            </select>
          </label>
        </div>

        <div className={`${styles.field} ${styles.fieldFull}`}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={form.requirePasswordChangeOnFirstLogin ?? true}
              onChange={e => setForm(prev => ({ ...prev, requirePasswordChangeOnFirstLogin: e.target.checked }))}
            />
            {t('requirePasswordChangeOnFirstLogin', { ns: 'admin' })}
          </label>
        </div>
      </div>

      <div className={styles.actions}>
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'admin' })}</button>
        <button className="btn btn-secondary" onClick={() => navigate('/members')} disabled={saving}>{t('cancel', { ns: 'common' })}</button>
      </div>
    </div>
  );
};

export default AddEmployee;